import React, { useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Tooltip,
  Typography,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DownloadIcon from "@mui/icons-material/Download";
import * as XLSX from "xlsx";
import sampleFile from "./update-iccid-validity.xlsx";
import api from "../../components/commonComponents/axiosSetup";
import { BASE_URL } from "../../config";

// ── helpers ──────────────────────────────────────────────────────────────────

const createEmptyRow = () => ({
  id: `${Date.now()}-${Math.random()}`,
  iccid: "",
  validity: "",
  iccidError: "", // inline per-row validation message
});

/** Validate a single ICCID value. Returns error string or "". */
const validateIccid = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "ICCID is required.";
  if (!/^\d+$/.test(trimmed)) return "ICCID must contain numbers only.";
  if (trimmed.length < 18 || trimmed.length > 20)
    return `ICCID must be 18 to 20 digits (${trimmed.length} entered).`;
  return "";
};

/** Normalise an Excel serial date or a date string to YYYY-MM-DD */
const normaliseDate = (raw) => {
  if (!raw) return "";

  // Excel numeric serial date
  if (typeof raw === "number") {
    const date = XLSX.SSF.parse_date_code(raw);
    if (!date) return "";
    const yyyy = date.y;
    const mm = String(date.m).padStart(2, "0");
    const dd = String(date.d).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  }

  const str = String(raw).trim();

  // Already YYYY-MM-DD
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;

  // DD/MM/YYYY or DD-MM-YYYY
  const dmy = str.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }

  // Native Date parse as last resort
  const parsed = new Date(str);
  if (!isNaN(parsed)) return parsed.toISOString().slice(0, 10);

  return str;
};

// ── component ─────────────────────────────────────────────────────────────────

const QuickIccidSearchModal = ({ open, onClose }) => {
  const [rows, setRows] = useState([createEmptyRow()]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [xlsxError, setXlsxError] = useState("");
  /** Set of ICCIDs returned as not-found by the API (for red border highlight) */
  const [notFoundIccids, setNotFoundIccids] = useState(new Set());
  const fileInputRef = useRef(null);

  // ── manual row helpers ──────────────────────────────────────────────────────

  const handleChange = (id, field, value) => {
    setRows((prev) =>
      prev.map((row) => {
        if (row.id !== id) return row;
        const updated = { ...row, [field]: value };
        // Live-validate ICCID as the user types (only numbers allowed — strip non-digits)
        if (field === "iccid") {
          // Strip non-digit characters on input
          const digitsOnly = value.replace(/\D/g, "");
          updated.iccid = digitsOnly;
          updated.iccidError = digitsOnly ? validateIccid(digitsOnly) : "";
        }
        return updated;
      })
    );
    setError("");
    setSuccess("");
    // Clear not-found highlight when user edits
    if (field === "iccid") setNotFoundIccids(new Set());
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, createEmptyRow()]);
  };

  const handleRemoveRow = (id) => {
    setRows((prev) =>
      prev.length === 1 ? prev : prev.filter((row) => row.id !== id)
    );
  };

  // ── excel upload ────────────────────────────────────────────────────────────

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file
    if (!file) return;

    setXlsxError("");
    setError("");
    setSuccess("");
    setNotFoundIccids(new Set());

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const workbook = XLSX.read(evt.target.result, {
          type: "binary",
          cellDates: false,
        });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];
        const raw = XLSX.utils.sheet_to_json(sheet, { defval: "" });

        if (!raw.length) {
          setXlsxError("The uploaded file is empty.");
          return;
        }

        // Accept headers case-insensitively
        const keys = Object.keys(raw[0]);
        const iccidKey = keys.find((k) => k.toLowerCase().includes("iccid"));
        const validityKey = keys.find(
          (k) =>
            k.toLowerCase().includes("validity") ||
            k.toLowerCase().includes("date")
        );

        if (!iccidKey || !validityKey) {
          setXlsxError(
            'Could not find "iccid" and "validity date" columns. Please check your Excel headers.'
          );
          return;
        }

        const parsed = raw
          .map((r) => {
            const iccid = String(r[iccidKey] ?? "").replace(/\D/g, "").trim();
            return {
              id: `${Date.now()}-${Math.random()}`,
              iccid,
              validity: normaliseDate(r[validityKey]),
              iccidError: iccid ? validateIccid(iccid) : "",
            };
          })
          .filter((r) => r.iccid);

        if (!parsed.length) {
          setXlsxError("No valid rows found in the uploaded file.");
          return;
        }

        const hasManualData = rows.some((r) => r.iccid || r.validity);
        setRows(hasManualData ? [...rows, ...parsed] : parsed);
      } catch (err) {
        console.error("Excel parse error:", err);
        setXlsxError(
          "Failed to parse the file. Please upload a valid .xlsx or .xls file."
        );
      }
    };
    reader.readAsBinaryString(file);
  };

  // ── submit ──────────────────────────────────────────────────────────────────

  const handleClose = () => {
    if (loading) return;
    setRows([createEmptyRow()]);
    setError("");
    setSuccess("");
    setXlsxError("");
    setNotFoundIccids(new Set());
    onClose();
  };

  const handleUpdate = async () => {
    setError("");
    setSuccess("");
    setNotFoundIccids(new Set());

    // Run full validation on all rows before submitting
    const validatedRows = rows.map((row) => ({
      ...row,
      iccidError: validateIccid(row.iccid),
    }));

    const hasIccidErrors = validatedRows.some((r) => r.iccidError);
    const hasMissingDate = validatedRows.some((r) => !r.validity);

    if (hasIccidErrors || hasMissingDate) {
      setRows(validatedRows);
      setError(
        hasIccidErrors
          ? "Please fix the ICCID errors highlighted below."
          : "Please enter a validity date for every row."
      );
      return;
    }

    const iccids = rows.map((row) => row.iccid.trim());
    if (new Set(iccids).size !== iccids.length) {
      setError("Duplicate ICCIDs are not allowed.");
      return;
    }

    const payload = rows.map(({ iccid, validity }) => ({
      iccid: iccid.trim(),
      validity,
    }));

    try {
      setLoading(true);
      const response = await api.put(
        `${BASE_URL}api/management/update/sim/validity`,
        { sims: payload }
      );

      if (
        response?.data?.success === false ||
        response?.data?.status === "ERROR"
      ) {
        throw new Error(response?.data?.message || "Failed to update validity.");
      }

      setSuccess(
        response?.data?.message ||
          `Validity updated for ${rows.length} SIM(s).`
      );
    } catch (err) {
      console.error("Validity update failed:", err);

      const apiData = err?.response?.data;

      // Highlight ICCIDs that the API says were not found
      if (Array.isArray(apiData?.body) && apiData.body.length > 0) {
        setNotFoundIccids(new Set(apiData.body.map((v) => String(v).trim())));
      }

      setError(
        apiData?.message ||
          err?.message ||
          "Failed to update SIM validity."
      );
    } finally {
      setLoading(false);
    }
  };

  // ── render ──────────────────────────────────────────────────────────────────

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "18px",
          overflow: "hidden",
          background: "linear-gradient(135deg, #f5f7ff, #eef3ff)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          background: "var(--bg)",
          color: "#fff",
          py: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Bulk ICCID Validity Update
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>
            Add multiple ICCIDs and their new validity dates
          </Typography>
        </Box>

        <IconButton
          onClick={handleClose}
          disabled={loading}
          sx={{ color: "#fff" }}
          aria-label="close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3, pt: 3 }}>

        {/* ── Excel upload strip ── */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            mb: 2.5,
            p: 2,
            borderRadius: "12px",
            background: "#fff",
            border: "1px dashed rgba(59,130,246,0.4)",
          }}
        >
          <UploadFileIcon sx={{ color: "#3b82f6", fontSize: 32 }} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="body2" fontWeight={600}>
              Upload Excel File
            </Typography>
            <Typography variant="caption" color="text.secondary">
              File must have <strong>iccid</strong> and{" "}
              <strong>validity date</strong> columns (xlsx / xls)
            </Typography>
          </Box>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx,.xls"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          <Tooltip title="Download sample template">
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              component="a"
              href={sampleFile}
              download="update-iccid-validity.xlsx"
              sx={{ borderRadius: "10px", textTransform: "none", whiteSpace: "nowrap" }}
            >
              Sample
            </Button>
          </Tooltip>

          <Tooltip title="Upload Excel (.xlsx / .xls)">
            <Button
              variant="outlined"
              size="small"
              startIcon={<UploadFileIcon />}
              onClick={() => fileInputRef.current?.click()}
              disabled={loading}
              sx={{ borderRadius: "10px", textTransform: "none", whiteSpace: "nowrap" }}
            >
              Choose File
            </Button>
          </Tooltip>
        </Box>

        {xlsxError && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            onClose={() => setXlsxError("")}
          >
            {xlsxError}
          </Alert>
        )}

        {/* ── Column headers ── */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1.5fr 1fr 44px" },
            gap: 1.5,
            mb: 1.5,
            px: 1,
          }}
        >
          <Typography fontWeight={700} variant="body2">
            ICCID
          </Typography>
          <Typography fontWeight={700} variant="body2">
            Validity Date
          </Typography>
          <Box />
        </Box>

        {/* ── Rows ── */}
        {rows.map((row, index) => {
          const isNotFound = notFoundIccids.has(row.iccid.trim());
          const hasIccidError = Boolean(row.iccidError) || isNotFound;

          return (
            <Box
              key={row.id}
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1.5fr 1fr 44px" },
                gap: 1.5,
                alignItems: "start",
                mb: 1.5,
                p: 1.5,
                borderRadius: "12px",
                background: "linear-gradient(135deg, #f5f7ff, #eef3ff)",
                border: hasIccidError
                  ? "1.5px solid #ef4444"
                  : "1px solid rgba(59,130,246,0.15)",
                transition: "border-color 0.2s",
              }}
            >
              <TextField
                fullWidth
                size="small"
                label={`ICCID ${index + 1}`}
                placeholder="Enter ICCID (min 20 digits)"
                value={row.iccid}
                onChange={(e) => handleChange(row.id, "iccid", e.target.value)}
                disabled={loading}
                error={hasIccidError}
                helperText={
                  isNotFound
                    ? "ICCID not found in the system."
                    : row.iccidError || ""
                }
                inputProps={{ maxLength: 20, inputMode: "numeric" }}
              />

              <TextField
                fullWidth
                size="small"
                label="Validity Date"
                type="date"
                value={row.validity}
                onChange={(e) =>
                  handleChange(row.id, "validity", e.target.value)
                }
                disabled={loading}
                InputLabelProps={{ shrink: true }}
              />

              <IconButton
                color="error"
                aria-label={`Remove ICCID row ${index + 1}`}
                onClick={() => handleRemoveRow(row.id)}
                disabled={loading || rows.length === 1}
                sx={{ mt: { xs: 0, sm: 0.25 } }}
              >
                <DeleteOutlineIcon />
              </IconButton>
            </Box>
          );
        })}

        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={handleAddRow}
          disabled={loading}
          sx={{ borderRadius: "10px", textTransform: "none", mb: 2 }}
        >
          Add Another ICCID
        </Button>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* ── Footer actions ── */}
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, mt: 1 }}
        >
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={loading}
            sx={{ borderRadius: "10px", textTransform: "none" }}
          >
            Close
          </Button>

          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={loading}
            sx={{ borderRadius: "10px", textTransform: "none", minWidth: 130 }}
          >
            {loading ? (
              <CircularProgress size={22} color="inherit" />
            ) : (
              `Update${rows.some((r) => r.iccid) ? ` (${rows.length})` : ""}`
            )}
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default QuickIccidSearchModal;
